import React from 'react';
import PropTypes from 'prop-types';
import { useMatches } from 'react-router-dom';
import { Tag, TagVariants } from '../Tag';
import * as S from './styled';

const getRouteName = (routeName, match) => {
  if (typeof routeName === 'function') {
    return routeName({ match });
  }
  return routeName;
};

// For splat routes (path ending in `/*`), match.pathname includes the splat-matched
// suffix (e.g. an open card), so the crumb would link back to the current view instead
// of the route's base. Strip the splat so the crumb targets the base path. react-router
// keeps pathname URL-encoded while it decodes the splat param (an id `a/b/c` appears as
// `a%2Fb%2Fc` in pathname), so decode pathname before comparing.
const getCrumbPathname = (match, tab) => {
  if (tab?.path) return tab.path;
  const pathname = match?.pathname;
  const splat = match?.params?.['*'];
  if (!pathname || !splat) return pathname;
  let decoded = pathname;
  try {
    decoded = decodeURIComponent(pathname);
  } catch {
    // malformed URI — fall back to the raw pathname
  }
  return decoded.endsWith(splat) ? decoded.slice(0, -splat.length).replace(/\/+$/, '') || '/' : pathname;
};

export const Breadcrumbs = ({ customerName, crumbs }) => {
  let matches = useMatches();
  matches = matches.filter(m => m.handle);
  const showCustomer = customerName && matches.some(m => m.handle.crumb?.showCustomer === true);

  if (!matches.length) return null;
  return (
    <S.CrumbsWrapper>
      {showCustomer && (
        <>
          <Tag content={customerName} variant={TagVariants.PRIMARY} />
          <S.Separator />
        </>
      )}
      {matches.map((match, index) => {
        const { params, handle } = match;
        const { crumb, routeName, tab } = handle || {};
        const { showAsLink, paramName } = crumb || {};
        const pathname = getCrumbPathname(match, tab);
        const param = params[paramName];
        const displayText = param || getRouteName(routeName, match);
        const isLast = index === matches.length - 1 && !crumbs?.length;
        const isLink = (showAsLink || (!!pathname && pathname !== '/')) && !isLast;

        return displayText ? (
          <React.Fragment key={pathname}>
            <S.CrumbContent>
              {isLink ? <S.GenericNavLink to={pathname}>{displayText}</S.GenericNavLink> : displayText}
            </S.CrumbContent>
            {!isLast && <S.Separator />}
          </React.Fragment>
        ) : null;
      })}
      {crumbs?.map((title, index) => {
        const isLast = index === crumbs.length - 1;
        return (
          <React.Fragment key={title}>
            <S.CrumbContent key={title}>{title}</S.CrumbContent>
            {!isLast && <S.Separator />}
          </React.Fragment>
        );
      })}
    </S.CrumbsWrapper>
  );
};

Breadcrumbs.propTypes = {
  customerName: PropTypes.string,
  crumbs: PropTypes.arrayOf(PropTypes.string)
};
